import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Sun, Moon } from "lucide-react";
import { LanguageSelector } from "./LanguageSelector";
import { useLang } from "../i18n/LanguageContext";
import { useTheme } from "../theme/ThemeContext";
import { LOGO } from "../data/content";

const links = [
  ["services", "nav_services"],
  ["industries", "nav_industries"],
  ["experience", "nav_experience"],
  ["team", "nav_team"],
  ["testimonials", "nav_testimonials"],
];

export const Nav = () => {
  const { t } = useLang();
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "glass border-b border-white/10" : "bg-transparent"}`}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 min-h-[5.5rem] py-3 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center">
          <span className="inline-flex items-center rounded-xl bg-white px-3.5 py-1.5 shadow-[0_0_20px_rgba(0,240,255,0.15)]">
            <img src={LOGO} alt="MACAS BV" className="h-14 w-auto" />
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map(([id, key]) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => go(id)}
              className="text-sm text-zinc-400 hover:text-white transition-colors font-mono uppercase tracking-wide"
            >
              {t(key)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            aria-label="Toggle theme"
            className="flex items-center justify-center w-10 h-10 rounded-full btn-ghost text-zinc-300"
          >
            {theme === "dark" ? <Sun size={16} className="text-cyan" /> : <Moon size={16} className="text-cyan" />}
          </button>
          <LanguageSelector />
          <button
            data-testid="nav-contact-cta"
            onClick={() => go("contact")}
            className="hidden sm:inline-flex btn-neon px-5 py-2.5 rounded-full text-sm font-semibold uppercase tracking-wide font-mono"
          >
            {t("nav_cta")}
          </button>
        </div>
      </div>
    </motion.header>
  );
};
